import React, { useEffect, useState } from "react";
import { FullscreenOutlined } from "@ant-design/icons";
import { Button, Empty, message, Modal, Select, Tooltip } from "antd";
import { Footer, Sidebar } from "../../../../components";
import { getGraphData } from "../../../../utils/api";
import VisualisationDropdowns from "./VisualisationDropdowns";
import { DetailedVisualisationTif } from "./DetailedVisualisationTif";

const SpinachVisualisationDashboard = ({
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  siteData,
  setSiteData,
  currentSite,
  setCurrentSite,
  siteLoading,
  setSiteLoading,
  currentCrop,
  setCurrentCrop,
  cropLoading,
  setCropLoading,
  cropData,
  setCropData,
  searchParams,
  setSearchParams,
  settingDefaults,
  setSettingDefaults,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [siteMetadata, setSiteMetadata] = useState([]);
  const [currentGreenhouse, setCurrentGreenhouse] = useState(null);
  const [currentPlot, setCurrentPlot] = useState(null);
  const [plotData, setPlotData] = useState([]);
  const [greenhouseData, setGreenhouseData] = useState([]);
  const [currentDate, setCurrentDate] = useState(null);
  const [tifType, setTifType] = useState(null);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [dateData, setDateData] = useState([]);
  const [headSizeGraphData, setHeadSizeGraphData] = useState({});
  const [standCountGraphData, setStandCountGraphData] = useState({});
  const [activeTab, setActiveTab] = useState("headSize");
  const [tifTypeOptions, setTifTypeOptions] = useState([]);
  const [messageText, setMessageText] = useState(null);
  const [isTifFullscreenOpen, setIsTifFullscreenOpen] = useState(false);
  const fullscreenInset = "clamp(8px, 2.2vw, 24px)";
  const fullscreenWrapperPadding = "clamp(4px, 1.1vw, 12px)";

  useEffect(() => {
    if (siteMetadata && currentCrop) {
      const tifTypeOptions = siteMetadata?.["tif_videos_options"] || [];
      if (tifTypeOptions.length === 0) {
        setMessageText("There is no tif to view");
      } else {
        setTifTypeOptions(tifTypeOptions);
        setTifType(tifTypeOptions[0]?.value);
        setMessageText(null);
      }
    }
  }, [siteMetadata, currentCrop, currentSite]);

  const distinguishAndSetGraphData = (data) => {
    const headSizeGraphData = {};
    const standCountGraphData = {};
    let activeTab = "standCount";

    Object.entries(data).forEach(([date, value]) => {
      const keys = Object.keys(value);
      const hasStandCount = keys.some(
        (key) => key.toLowerCase() === "standcount",
      );
      if (hasStandCount) {
        standCountGraphData[date] = value;
      } else {
        headSizeGraphData[date] = value;
        activeTab = "headSize";
      }
    });
    setHeadSizeGraphData(headSizeGraphData);
    setStandCountGraphData(standCountGraphData);
    setActiveTab(activeTab);
  };

  // Fetch graph data
  const fetchGraphData = async () => {
    setGraphLoading(true);
    try {
      const graphRes = await getGraphData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        currentPlot.id,
        null,
        currentGreenhouse.id,
      );
      const data = graphRes?.data?.data?.[currentPlot.name] || [];
      const grapDataDateWise = {};
      data.forEach((dateWiseData) => {
        grapDataDateWise[dateWiseData.harvest_date] = dateWiseData.data;
      });
      distinguishAndSetGraphData(grapDataDateWise);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Oops! Error fetching graph data!";
      messageApi.open({
        type: "error",
        content: msg,
      });
    } finally {
      setGraphLoading(false);
    }
  };

  useEffect(() => {
    if (currentPlot && currentDate && !dropdownLoading) {
      fetchGraphData();
    }
  }, [currentPlot, currentDate, dropdownLoading]);

  return (
    <>
      <div id="dashboard">
        <Sidebar
          active="visualisation"
          sidebarType="SpinachGrower"
          open={sidebarOpen}
          setOpen={setSidebarOpen}
        />
        <div
          className={`flex w-full flex-col pb-14 pt-8 lg:pt-6 sm:pt-4 ${
            sidebarOpen ? "pl-[210px] lg:pl-[50px]" : "pl-[50px]"
          } transition-[padding] duration-200 ease-in-out`}
        >
          <div className="mx-auto w-full max-w-[1900px] px-8 xl:px-6 lg:px-3 md:px-2 sm:px-1.5">
            {contextHolder}
            <VisualisationDropdowns
              siteMetadata={siteMetadata}
              setSiteMetadata={setSiteMetadata}
              currentOrg={currentOrg}
              setCurrentOrg={setCurrentOrg}
              orgData={orgData}
              setOrgData={setOrgData}
              orgLoading={orgLoading}
              setOrgLoading={setOrgLoading}
              currentSite={currentSite}
              setCurrentSite={setCurrentSite}
              siteData={siteData}
              setSiteData={setSiteData}
              siteLoading={siteLoading}
              setSiteLoading={setSiteLoading}
              currentCrop={currentCrop}
              setCurrentCrop={setCurrentCrop}
              cropData={cropData}
              setCropData={setCropData}
              cropLoading={cropLoading}
              setCropLoading={setCropLoading}
              currentPlot={currentPlot}
              setCurrentPlot={setCurrentPlot}
              currentGreenhouse={currentGreenhouse}
              setCurrentGreenhouse={setCurrentGreenhouse}
              greenhouseData={greenhouseData}
              setGreenhouseData={setGreenhouseData}
              plotData={plotData}
              setPlotData={setPlotData}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              setDropdownLoading={setDropdownLoading}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              settingDefaults={settingDefaults}
              setSettingDefaults={setSettingDefaults}
              dateData={dateData}
              setDateData={setDateData}
            />
            {Object.keys(siteMetadata).length === 0 && !dropdownLoading ? (
              <div className="text-2xl text-center">
                Metadata not Configured!
              </div>
            ) : !dropdownLoading && messageText ? (
              <Empty
                className="font-bold text-primaryBlue h-[70vh] content-center"
                description={messageText}
              ></Empty>
            ) : !dropdownLoading ? (
              <div>
                <div className="flex flex-row justify-end items-center mb-4">
                  <Select
                    defaultValue={tifType}
                    value={tifType}
                    onChange={setTifType}
                    options={tifTypeOptions}
                    style={{ width: 200 }}
                  />
                </div>
                <div className="relative flex flex-col">
                  <Tooltip title="Open fullscreen">
                    <Button
                      type="text"
                      aria-label="Open detailed visualisation TIF in fullscreen"
                      icon={<FullscreenOutlined />}
                      onClick={() => setIsTifFullscreenOpen(true)}
                      className="!absolute right-2 top-2 z-20 !text-primaryBlue"
                    />
                  </Tooltip>
                  <DetailedVisualisationTif
                    currentOrg={currentOrg}
                    currentSite={currentSite}
                    siteMetadata={siteMetadata}
                    currentCrop={currentCrop}
                    currentGreenhouse={currentGreenhouse}
                    currentPlot={currentPlot}
                    currentDate={currentDate}
                    tifType={tifType}
                  />
                </div>
                <Modal
                  open={isTifFullscreenOpen}
                  onCancel={() => setIsTifFullscreenOpen(false)}
                  footer={null}
                  title={null}
                  width={`calc(100% - ${fullscreenInset})`}
                  centered={true}
                  destroyOnHidden={true}
                  styles={{
                    wrapper: {
                      overflow: "hidden",
                      padding: fullscreenWrapperPadding,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                    body: {
                      padding: 0,
                      height: `calc(100dvh - ${fullscreenInset})`,
                      overflow: "hidden",
                    },
                    content: {
                      padding: 0,
                      height: `calc(100dvh - ${fullscreenInset})`,
                      overflow: "hidden",
                    },
                  }}
                  style={{
                    paddingBottom: 0,
                    maxWidth: "none",
                    margin: "0 auto",
                  }}
                  className="!max-w-none"
                >
                  <div className="h-full min-h-0 overflow-hidden p-2 lg:p-1.5 md:p-1 sm:p-0.5">
                    <DetailedVisualisationTif
                      currentOrg={currentOrg}
                      currentSite={currentSite}
                      siteMetadata={siteMetadata}
                      currentCrop={currentCrop}
                      currentGreenhouse={currentGreenhouse}
                      currentPlot={currentPlot}
                      currentDate={currentDate}
                      tifType={tifType}
                      isFullscreen={true}
                    />
                  </div>
                </Modal>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        </div>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    </>
  );
};

export default SpinachVisualisationDashboard;
